import { Link } from "react-router-dom";
import { hotMovies, newEpisodes, categoryMovieMap } from "@/mocks/movies";
import { categories } from "@/mocks/categories";
import { articles } from "@/mocks/articles";
import { OrganizationSchema, WebsiteSchema, WebPageSchema } from "@/components/feature/SEOHead";

function MovieCard({ movie }: { movie: typeof hotMovies[0] }) {
  return (
    <Link
      to={`/article/${movie.id}`}
      className="group block cursor-pointer"
    >
      <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-3 bg-background-200">
        <img
          src={movie.cover}
          alt={movie.title}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute top-2 left-2 flex flex-wrap gap-1.5">
          {movie.isHot && (
            <span className="px-2 py-0.5 text-xs bg-primary-500 text-background-50 rounded-md whitespace-nowrap">
              热播
            </span>
          )}
          {movie.isNew && (
            <span className="px-2 py-0.5 text-xs bg-accent-500 text-background-50 rounded-md whitespace-nowrap">
              新上
            </span>
          )}
          {movie.rating > 0 && (
            <span className="px-2 py-0.5 text-xs bg-background-950/70 text-accent-300 rounded-md whitespace-nowrap">
              {movie.rating}
            </span>
          )}
        </div>
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-background-950/80 to-transparent p-3">
          <p className="text-xs text-background-200 whitespace-nowrap">
            {movie.updateInfo}
          </p>
        </div>
      </div>
      <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors duration-200 line-clamp-1">
        {movie.title}
      </h3>
      <p className="text-xs text-foreground-500 mt-1 line-clamp-1">
        {movie.actors.slice(0, 3).join(" / ")}
      </p>
    </Link>
  );
}

function CategoryCard({ cat }: { cat: typeof categories[0] }) {
  const count = categoryMovieMap[cat.slug]?.length || 0;
  return (
    <Link
      to={`/category/${cat.slug}`}
      className="group flex flex-col items-center p-6 rounded-xl bg-background-100 hover:bg-primary-50 transition-colors duration-300 cursor-pointer"
    >
      <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-3 group-hover:bg-primary-500 group-hover:text-background-50 transition-all duration-300">
        <i className={`${cat.icon} text-xl md:text-2xl`}></i>
      </div>
      <span className="text-sm md:text-base font-medium text-foreground-900 whitespace-nowrap">
        {cat.name}
      </span>
      <span className="text-xs text-foreground-500 mt-1 whitespace-nowrap">
        {count} 部作品
      </span>
    </Link>
  );
}

function ArticleCard({ article }: { article: typeof articles[0] }) {
  return (
    <Link
      to={`/article/${article.id}`}
      className="group flex flex-col sm:flex-row gap-4 p-4 rounded-xl bg-background-100 hover:bg-background-200 transition-colors duration-200 cursor-pointer"
    >
      <div className="w-full sm:w-48 h-28 rounded-lg overflow-hidden flex-shrink-0 bg-background-200">
        <img
          src={article.coverImage}
          alt={article.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="text-sm md:text-base font-medium text-foreground-900 group-hover:text-primary-600 transition-colors duration-200 line-clamp-2">
          {article.title}
        </h3>
        <p className="text-xs md:text-sm text-foreground-500 mt-2 line-clamp-2">
          {article.summary}
        </p>
        <div className="flex items-center gap-3 mt-3">
          <span className="text-xs text-foreground-400 whitespace-nowrap">
            {article.date}
          </span>
          <div className="flex flex-wrap gap-1.5">
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="px-2 py-0.5 text-xs bg-secondary-100 text-secondary-700 rounded-md whitespace-nowrap"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      </div>
    </Link>
  );
}

export default function Home() {
  return (
    <>
      <OrganizationSchema />
      <WebsiteSchema />
      <WebPageSchema
        title="木瓜影视 - 高清不缓冲在线观看不卡顿：免费无广告极速播放"
        description="木瓜影视提供高清不缓冲在线观看体验，不卡顿无广告极速播放，涵盖最新电视剧和电影高清免费在线看，畅享流畅观影。"
        url="https://www.muguays.com/"
        dateModified="2026-08-02"
      />

      <header>
        <section className="relative w-full h-[420px] md:h-[600px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Chinese%20cinema%20atmosphere%20with%20warm%20red%20curtains%20and%20film%20projector%20light%2C%20abstract%20artistic%20movie%20theater%20background%2C%20soft%20dramatic%20lighting%2C%20dark%20elegant%20tones%2C%20cinematic%20style&width=1920&height=1080&seq=hero-bg-01&orientation=landscape"
            alt="木瓜影视 - 高清在线观影"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background-950/60 via-background-950/40 to-background-950/70"></div>
          <div className="relative z-10 text-center px-4 w-full">
            <h1 className="font-heading text-2xl md:text-4xl text-background-50 mb-4">
              高清不缓冲，在线观看不卡顿
            </h1>
            <p className="text-sm md:text-base text-background-200 max-w-2xl mx-auto mb-6 leading-relaxed">
              木瓜影视提供高清不缓冲在线观看体验，免费无广告极速播放，涵盖最新电视剧和电影高清免费在线看，畅享流畅观影。
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/category/movie"
                className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm rounded-lg transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                立即观影
              </Link>
              <Link
                to="/category/tv"
                className="px-6 py-2.5 bg-background-50/20 hover:bg-background-50/30 text-background-50 text-sm rounded-lg backdrop-blur-sm transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                热门剧集
              </Link>
            </div>
          </div>
        </section>
      </header>

      <section className="px-4 md:px-6 py-10 md:py-14">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-lg md:text-xl text-foreground-900">
              内容分类
            </h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
            {categories.map((cat) => (
              <CategoryCard key={cat.slug} cat={cat} />
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-lg md:text-xl text-foreground-900">
              热播推荐
            </h2>
            <Link
              to="/category/movie"
              className="text-sm text-primary-600 hover:text-primary-700 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              查看更多 <i className="ri-arrow-right-line ml-1"></i>
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
            {hotMovies.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 md:px-6 py-10 md:py-14">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-lg md:text-xl text-foreground-900">
              最新更新
            </h2>
            <Link
              to="/category/tv"
              className="text-sm text-primary-600 hover:text-primary-700 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              查看更多 <i className="ri-arrow-right-line ml-1"></i>
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
            {newEpisodes.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-lg md:text-xl text-foreground-900">
              影视资讯
            </h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-5">
            {articles.map((article) => (
              <ArticleCard key={article.id} article={article} />
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 md:px-6 py-10 md:py-14">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="font-heading text-lg md:text-xl text-foreground-900 mb-3">
            为什么选择木瓜影视？
          </h2>
          <p className="text-sm text-foreground-500 mb-8 max-w-2xl mx-auto">
            致力于为观众提供最优质的在线观影体验
          </p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {[
              { icon: "ri-4k-line", title: "高清画质", desc: "支持1080P至4K超清画质，画面清晰细腻" },
              { icon: "ri-speed-up-line", title: "极速播放", desc: "全球CDN加速，不缓冲不卡顿，流畅观看" },
              { icon: "ri-advertisement-line", title: "无广告", desc: "纯净观影体验，全程无任何广告打扰" },
              { icon: "ri-currency-line", title: "完全免费", desc: "所有内容免费观看，无需会员无需付费" },
            ].map((item) => (
              <div key={item.title} className="p-5 rounded-xl bg-background-100">
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mx-auto mb-3">
                  <i className={`${item.icon} text-lg`}></i>
                </div>
                <h3 className="text-sm font-medium text-foreground-900 mb-1 whitespace-nowrap">
                  {item.title}
                </h3>
                <p className="text-xs text-foreground-500 leading-relaxed">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}